import { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import ProfileNavbar from './ProfileNavbar';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import whiteLabelService from '../../services/whiteLabelService';
import SubscriptionCheck from '../WhiteLabel/SubscriptionCheck';
import InstanceSetup from '../WhiteLabel/InstanceSetup';
import InstanceManagement from '../WhiteLabel/InstanceManagement';
import { LoadingState, ErrorState, SubscriptionProcessingState } from '../WhiteLabel/WorkflowStates';
import './scss/WhiteLabelSettings.scss';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUser } from '../../redux/Slices/thunks';
import { showSuccess, showError } from '../../utils/snackbarHelpers';

// Workflow states for better organization
const WORKFLOW_STATES = {
  LOADING: 'loading',
  NO_SUBSCRIPTION: 'no_subscription',
  SUBSCRIPTION_ACTIVE: 'subscription_active',
  NO_INSTANCE: 'no_instance',
  INSTANCE_SETUP: 'instance_setup',
  INSTANCE_ACTIVE: 'instance_active',
  ERROR: 'error'
};

const WhiteLabelSettings = () => {
  const { userName } = useParams();
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { refreshBranding } = useWhiteLabel();

  // Check if the current user is accessing their own white-label settings
  const isOwnSettings = user?.user?.username === userName;

  const [config, setConfig] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [optimisticPurchase, setOptimisticPurchase] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [optimisticStatus, setOptimisticStatus] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);

  // Determine current workflow state
  const getWorkflowState = () => {
    if (loading) return WORKFLOW_STATES.LOADING;
    if (error && !config && !paymentStatus) return WORKFLOW_STATES.ERROR;
    if (optimisticPurchase) return WORKFLOW_STATES.SUBSCRIPTION_ACTIVE;

    // Use payment status if available (more reliable than user object)
    if (paymentStatus) {
      if (!paymentStatus.hasAddon) {
        return WORKFLOW_STATES.NO_SUBSCRIPTION;
      }

      if (paymentStatus.canCreateInstance) {
        return WORKFLOW_STATES.NO_INSTANCE;
      }

      if (paymentStatus.instance) {
        return WORKFLOW_STATES.INSTANCE_ACTIVE;
      }
    }

    // Fallback to old logic for backward compatibility
    if (!user?.user?.whiteLabelAddon || config?.needsPurchase) {
      return WORKFLOW_STATES.NO_SUBSCRIPTION;
    }

    if (config?.hasWhiteLabel === false) {
      return WORKFLOW_STATES.NO_INSTANCE;
    }

    if (config?.hasWhiteLabel === true) {
      return WORKFLOW_STATES.INSTANCE_ACTIVE;
    }

    // Default to loading if we don't have enough info
    return WORKFLOW_STATES.LOADING;
  };

  const workflowState = getWorkflowState();

  // Load white label configuration and payment status
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load both payment status and config
  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Refresh user data to get latest trial status
      await dispatch(fetchUser()).unwrap();

      // Load payment status (may lag server state briefly)
      const paymentStatusResponse = await whiteLabelService.getPaymentStatus();
      console.log('Payment status loaded:', paymentStatusResponse);
      setPaymentStatus(paymentStatusResponse);

      // Always fetch config next; it contains authoritative trial/subscription flags
      await loadConfig();
    } catch (err) {
      console.error('Error loading initial data:', err);
      // Fallback to loading config anyway so UI can reflect the latest state
      await loadConfig();
    }
  };

  // Function to handle purchase success with optimistic updates
  const handlePurchaseSuccess = async () => {

    // Set optimistic state to show loading/success state
    setOptimisticPurchase(true);

    // Refresh user data first
    dispatch(fetchUser());

    // Retry logic to wait for backend webhook processing
    const retryLoadPaymentStatus = async (attempts = 0, maxAttempts = 12) => {
      try {
        // Refresh user data first
        dispatch(fetchUser());

        const paymentStatusResponse = await whiteLabelService.getPaymentStatus();

        // Check if user now has the addon
        if (paymentStatusResponse.hasAddon) {
          setPaymentStatus(paymentStatusResponse);
          setOptimisticPurchase(false);

          // Also refresh the config to ensure we have the latest state
          await loadConfig();

          // Payment successful! You can now create your white label instance.
          return;
        }

        // If payment not processed yet and we have attempts left, retry
        if (attempts < maxAttempts - 1) {
          setTimeout(() => retryLoadPaymentStatus(attempts + 1, maxAttempts), 3000);
        } else {
          await loadInitialData();
          setOptimisticPurchase(false);
        }
      } catch (error) {
        if (attempts < maxAttempts - 1) {
          setTimeout(() => retryLoadPaymentStatus(attempts + 1, maxAttempts), 3000);
        } else {
          await loadInitialData();
          setOptimisticPurchase(false);
          showError(dispatch, 'Error verifying payment status. Please refresh the page.');
        }
      }
    };

    // Start retry process after a short delay to allow webhook processing
    setTimeout(() => retryLoadPaymentStatus(), 2000);
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await whiteLabelService.getConfig();

      // Handle case where service indicates purchase is needed
      if (response.needsPurchase) {
        dispatch(fetchUser());
      }

      setConfig(response);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error loading white label config:', err);

      // Handle different error scenarios
      if (err.message.includes('No white label configuration found') || err.status === 404) {
        setConfig({ hasWhiteLabel: false });
        setError(null); // Don't show error for this case
      } else if (err.status === 403 && err.message?.includes('White label addon required')) {
        // User's subscription was cancelled - show purchase option
        setConfig({ hasWhiteLabel: false, needsPurchase: true });
        setError(null);
        // Force refresh user data to ensure we have latest subscription status
        dispatch(fetchUser());
      } else if (err.status === 403) {
        // Other permission errors
        setError('You do not have permission to access white label settings.');
      } else if (err.status === 500) {
        setError('Server error occurred. Please try again later.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to load white label configuration.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWhiteLabel = async (data) => {
    try {
      setSaving(true);
      const response = await whiteLabelService.create(data);

      // Update both config and payment status
      setConfig({
        hasWhiteLabel: true,
        config: response.whiteLabel
      });

      // Refresh payment status to reflect the new instance
      const updatedPaymentStatus = await whiteLabelService.getPaymentStatus();
      setPaymentStatus(updatedPaymentStatus);

      // Force refresh branding after creating white label
      await refreshBranding(true);
      setError(null);
      // White label instance created successfully
    } catch (err) {
      console.error('Error creating white label:', err);

      // Check if error is due to missing addon
      if (err.message?.includes('need to purchase') || err.message?.includes('addon required')) {
        // Refresh payment status to get current state
        const currentPaymentStatus = await whiteLabelService.getPaymentStatus();
        setPaymentStatus(currentPaymentStatus);
      }

      const errorMessage = err.message || 'Failed to create white label instance. Please try again.';
      setError(errorMessage);
      showError(dispatch, errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateConfig = async (updates) => {
    try {
      setSaving(true);

      // Optimistic update - update config immediately
      if (config?.config) {
        setConfig(prevConfig => ({
          ...prevConfig,
          config: {
            ...prevConfig.config,
            ...updates
          }
        }));
      }

      const response = await whiteLabelService.updateConfig(updates);

      // Update with server response to ensure consistency
      setConfig(prevConfig => ({
        ...prevConfig,
        config: {
          ...prevConfig.config,
          ...response.whiteLabel
        }
      }));

      // Only refresh branding if branding-related updates were made
      if (updates.branding || updates.status) {
        await refreshBranding(true);
      }

      setError(null);
      // Configuration updated successfully - auto-save handles user feedback
    } catch (err) {
      console.error('Error updating white label config:', err);
      const errorMessage = err.message || 'Failed to update configuration. Please try again.';
      setError(errorMessage);
      // Error handling - auto-save components will show their own error states
      // Revert optimistic update on error
      await loadConfig();
    } finally {
      setSaving(false);
    }
  };

  const handleToggleWhiteLabel = async (enabled) => {
    const newStatus = enabled ? 'active' : 'inactive';
    setToggleLoading(true);
    setOptimisticStatus(newStatus);

    try {
      const response = await whiteLabelService.updateConfig({
        status: newStatus
      });

      // Update config with server response, ensuring status is preserved
      setConfig(prevConfig => ({
        ...prevConfig,
        config: {
          ...prevConfig.config,
          ...response.whiteLabel,
          status: newStatus // Ensure status is what we expect
        }
      }));

      // Force refresh branding when toggling status
      await refreshBranding(true);

      // Clear optimistic state on success
      setOptimisticStatus(null);
      setError(null);
      showSuccess(dispatch, `White label ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
    } catch (err) {
      console.error('Error toggling white label status:', err);
      const errorMessage = err.message || `Failed to ${newStatus === 'active' ? 'activate' : 'deactivate'} white label. Please try again.`;
      setError(errorMessage);
      showError(dispatch, errorMessage);
      setOptimisticStatus(null);
    } finally {
      setToggleLoading(false);
    }
  };

  // Render based on workflow state
  const renderWorkflowState = () => {
    switch (workflowState) {
      
      case WORKFLOW_STATES.LOADING:
        return <LoadingState />;

      case WORKFLOW_STATES.ERROR:
        return <ErrorState error={error} onRetry={loadConfig} />;

      case WORKFLOW_STATES.NO_SUBSCRIPTION:
        return <SubscriptionCheck onPurchaseSuccess={handlePurchaseSuccess} />;

      case WORKFLOW_STATES.SUBSCRIPTION_ACTIVE:
        return <SubscriptionProcessingState />;

      case WORKFLOW_STATES.NO_INSTANCE:
        return (
          <InstanceSetup
            onSubmit={handleCreateWhiteLabel}
            loading={saving}
            user={user}
            paymentStatus={paymentStatus}
          />
        );

      case WORKFLOW_STATES.INSTANCE_ACTIVE:
        return (
          <InstanceManagement
            config={config}
            user={user}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            onUpdateConfig={handleUpdateConfig}
            onToggleWhiteLabel={handleToggleWhiteLabel}
            optimisticStatus={optimisticStatus}
            toggleLoading={toggleLoading}
            saving={saving}
            error={error}
            setError={setError}
            onCancelSuccess={handleCancelSuccess}
            onRefresh={loadInitialData}
          />
        );

      default:
        return <ErrorState error="Unknown workflow state" onRetry={loadConfig} />;
    }
  };

  const handleCancelSuccess = async () => {
    // Update user state and reload data
    dispatch(fetchUser());
    setConfig(null);
    setPaymentStatus(null);
    setError(null);

    // Reload initial data to get current state
    await loadInitialData();
  };

  // Redirect if user is trying to access someone else's white-label settings
  if (!isOwnSettings) {
    return <Navigate to={`/u/${user?.user?.username}/white-label`} replace />;
  }

  return (
    <>
      <ProfileNavbar />
      <div className="white-label-settings">
        {renderWorkflowState()}
      </div>
    </>
  );
};



export default WhiteLabelSettings;
